import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { createSafeStorage } from './safe-storage';

export type Theme = 'light' | 'dark';

type PreferencesState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
};

/**
 * Durable user preferences. Theme lives here as the single owner; ephemeral chrome state (the
 * sidebar open/collapsed flag) is owned by the shadcn `SidebarProvider` and its cookie, not a
 * store, and is deliberately not persisted here. Locale is persisted separately under
 * `management.locale` as a plain string, since first paint reads it before any store hydrates.
 */
export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      toggleTheme: () => {
        get().setTheme(get().theme === 'light' ? 'dark' : 'light');
      },
    }),
    {
      name: 'management.preferences',
      storage: createJSONStorage(createSafeStorage),
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme);
      },
    },
  ),
);

/** Class-based dark mode: the `.dark` class is what `@custom-variant` keys off. */
export function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}
